import { Component, HostListener, OnDestroy, OnInit, ViewChild, } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import * as constants from 'src/app/config/app-constants';
import { ToastPanelType } from 'src/app/config/app-constants';
import { AssetService } from 'src/app/external-user/inventory/services/asset.service';
import { Organization } from 'src/app/internal-user/customer-management/models/organization';
import { SearchBarComponent } from 'src/app/shared/components/search-bar/search-bar.component';
import { GenericConfirmationDialogComponent } from 'src/app/shared/generic-confirmation-dialog/generic-confirmation-dialog.component';
import { NoDataRow, TableGroup } from 'src/app/shared/models/ui/table-group';
import { ToastService } from 'src/app/shared/services/toast.service';
import { FleetManagerActions } from 'src/app/store/actions';
import { getProvision } from 'src/app/store/selectors/fleet-manager.selector';
import { selectSites } from 'src/app/store/selectors/organizations.selector';
import { State } from 'src/app/store/state';
import { DeploymentAction, Device, ExposedActionOperation, System, SystemAction } from '../../models/device.model';
import { getLogOptions } from '../../models/getLogOptions.model';
import { FleetConfigurationService } from '../../services/fleet-configuration.service';
import { FleetManagementService } from '../../services/fleet-management.service';
import { SystemFormMetadataService } from '../../services/system-form-metadata.service';
import { CertManagerDialogComponent, CertManagerDialogData } from '../cert-manager-dialog/cert-manager-dialog.component';
import { DeploymentFormDialogComponent, DeploymentFormDialogComponentData } from '../deployment-form-dialog/deployment-form-dialog.component';
import { EdgeApplianceDetailsComponent } from '../edge-appliance-details/edge-appliance-details.component';

interface TableRow extends TableGroup, NoDataRow, System {
  index?: number;
}
// basic select
interface SortItemMdl {
  active: string;
  fieldDispName: string;
  sortDirection: string;
  direction: string;
}

interface KeyValue {
  Key: string;
  Value: string;
}

@Component({
    selector: 'app-provisioned',
    templateUrl: './provisioned.component.html',
    styleUrls: ['./provisioned.component.scss'],
    standalone: false
})

export class ProvisionedComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatAccordion) accordion: MatAccordion;
  @ViewChild(SearchBarComponent, { static: true }) childComponent: SearchBarComponent;
  filterText = '';
  displayedColumns: string[] = ['name', 'site', 'id', 'status', 'heartbeat', 'actions'];
  // Tree handling
  showHierarchy: boolean = true; // feature toggle to display new tree
  provisioned: any;
  selectedOrgId: string = '';
  selectedStatusId: string = '';
  provisionedList = new MatTableDataSource();
  packagerList = new MatTableDataSource<TableRow>([]);
  rootDevices: Device[] = []; // roots used for hierarchical rendering
  displayedRootDevices: Device[] = [];
  childDeploymentsExpanded: Set<string> = new Set<string>(); // tracks which parent devices have expanded child sections
  currentOrgElement: Device;
  currentOrgHeader: string;
  provisionedListData: Device[] = [];
  statusList: KeyValue[] = [];
  orgDDList: KeyValue[] = [];
  downloadingLogs = false;
  windowWidth = window.innerWidth;
  accordionCollapsed = false;
  sites: Organization[];
  deviceSubscription: Subscription;
  siteSubscription: Subscription;
  /** Ordered list of system type metadata (uppercase name + numeric systemType) derived from metadata. */
  systemTypes: { nameUpper: string; systemType: number }[] = [];
  machineTypes: any[] = [];
  private readonly certManagerActionName = 'ManageCertificates';
  private readonly certManagerControlType = 'custom';
  private readonly certManagerControlKey = 'cert-manager';
  /** Tracks device IDs for which we already attempted one-time deployment-actions fallback fetch. */
  private readonly deploymentActionsChecked = new Set<string>();
  /** Stable, normalized deployment actions cache keyed by edgeDeviceId. */
  private readonly deploymentActionsCache = new Map<string, DeploymentAction[]>();
  /** Stable, normalized system actions cache keyed by system row object. */
  private readonly systemActionsCache = new Map<System, SystemAction[]>();
  private disableDeviceLoaderSequence = 0;

  @HostListener('window:resize')
  onResize() {
    this.windowWidth = window.innerWidth;
    this.calculateOffset();
  }

  offsetFromBottom: number;

  private calculateOffset() {
    const windowHeight = window.innerHeight;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    // Set the offset value based on your calculation
    this.offsetFromBottom = windowHeight - scrollTop - 245;
  }

  constructor(public dialog: MatDialog,
    public store: Store<State>,
    public fleetManagementService: FleetManagementService,
    public fleetConfigurationService: FleetConfigurationService,
    public assetService: AssetService,
    public toastService: ToastService,
    private readonly ngxLoader: NgxUiLoaderService,
    private readonly systemFormMetadataService: SystemFormMetadataService) {
    this.loadProvisionedData();
    this.loadSites();
    this.initSystemTypes();
  }

  /** Depth-first walk across all devices including nested children. */
  private walkDevices(devices: Device[], fn: (d: Device) => void) {
    if (!Array.isArray(devices)) { return; }
    devices.forEach(d => {
      fn(d);
      if (Array.isArray(d.children) && d.children.length) {
        this.walkDevices(d.children, fn);
      }
    });
  }

  /** Sort root devices (and their children recursively) by org then friendly name / id. */
  private sortDevices(devices: Device[]): Device[] {
    const cmp = (a: Device, b: Device) => {
      const orgCmp = (a.organizationId || '').localeCompare(b.organizationId || '');
      if (orgCmp !== 0) return orgCmp;
      return this.getDeviceDisplayName(a).localeCompare(this.getDeviceDisplayName(b));
    };
    const sorted = [...devices].sort(cmp);
    // Recursively sort children
    this.walkDevices(sorted, d => {
      if (d.children) {
        d.children = this.sortDevices(d.children); // reuse same ordering logic
      }
    });
    return sorted;
  }

  /** Sort systems for a single device. */
  private sortSystems(device: Device) {
    if (!device.systems) { return; }
    device.systems.sort((a, b) => {
      return this.displaySite(a.siteId).localeCompare(this.displaySite(b.siteId)) || (a.systemFriendlyName || '').localeCompare(b.systemFriendlyName || '');
    });
  }

  ngOnInit() {
    this.calculateOffset();
    this.bindStatusDropdown();
    this.bindOrgDropdown();
    this.assetService.getActiveMachinesType().subscribe(machineTypes => {
      this.machineTypes = machineTypes;
    });
  }

  ngOnDestroy(): void {
    if (this.deviceSubscription) {
      this.deviceSubscription.unsubscribe();
    }

    if (this.siteSubscription) {
      this.siteSubscription.unsubscribe();
    }
  }

  /** Initialize system type metadata (static one-time load). */
  private initSystemTypes(): void {
    this.systemFormMetadataService.loadMetadataIfNeeded().subscribe({
      next: () => {
        this.systemTypes = this.systemFormMetadataService.getSystemTypes();
      },
      error: err => console.error('[ProvisionedComponent] Failed to load system form metadata.', err)
    });
  }

  loadProvisionedData() {
    this.deviceSubscription = this.store.select(getProvision).subscribe(res => {
      if (res) {
          this.deploymentActionsCache.clear();
          this.deploymentActionsChecked.clear();
          this.systemActionsCache.clear();
            // Sort devices by org name
          console.debug('[ProvisionedComponent] Raw provisioned devices from store:', res);
          this.provisioned = this.sortDevices(res);
          this.rootDevices = this.provisioned; // cache roots
          this.displayedRootDevices = [...this.rootDevices];

          // Sort each device's systems by site name followed by friendly name
          this.walkDevices(this.provisioned, device => this.sortSystems(device));

          this.provisionedListData = this.provisioned; // maintain legacy list reference
          this.provisionedList.data = this.provisionedListData;
          console.debug('[ProvisionedComponent] Processed provisioned root devices count:', this.rootDevices.length);

          this.bindStatusDropdown();
          this.bindOrgDropdown();
      }
    });
  }

  loadSites() {
    this.siteSubscription = this.store.select(selectSites).subscribe({
      next: sites => {
        this.sites = sites;
      },
      error: err => {
        console.log(err);
        this.toastService.openToast('Error fetching site names', constants.ToastPanelType.error);
      }
    })
  }

  displaySite(siteId: string): string {
    return this?.sites?.find(site => site.customerNo === siteId)?.name || 'N/A';
  }

  toggleRow(element: Device) {
    this.packagerList.data = element.systems;
    this.currentOrgElement = element;
    this.currentOrgHeader = element.organizationId + "- [" + element.edgeDeviceId + "]";
    this.currentOrgElement.enabled = true;
  }

  applyRowClass(row: any): boolean {
    return row && row.status !== 'Online';
  }

  applyColumnClass(element: any): boolean {
    return element && element.status !== 'Online';
  }

  bindStatusDropdown() {
    this.statusList = [{ Key: "all", Value: "All Statuses" }];
    const uniqueStatusIds = new Set();
    this.provisionedListData.forEach((t) => {
      if (!uniqueStatusIds.has(t.connectionState)) {
        this.statusList.push({ Key: t.connectionState.toString(), Value: t.connectionState });
        uniqueStatusIds.add(t.connectionState);
        t.systems.forEach((t) => {
          if (!uniqueStatusIds.has(t.status)) {
            this.statusList.push({ Key: t.status.toString(), Value: t.status });
            uniqueStatusIds.add(t.status);
          }
        });
      }
    });
    // Sort the statusList alphabetically based on the 'Value' (except "All Statuses")
    this.statusList.sort((a, b) => {
      if (a.Value === "All Statuses") return -1; // "All Statuses" comes first
      if (b.Value === "All Statuses") return 1; // "All Statuses" comes first
      return a.Value.localeCompare(b.Value); // Sort other options alphabetically
    });

    this.selectedStatusId = this.statusList[0].Key;
  }

  bindOrgDropdown() {
    this.orgDDList = [{ Key: "all", Value: "All Organizations" }];
    const uniqueCustomerIds = new Set();
    this.provisionedListData.forEach((t) => {
      if (!uniqueCustomerIds.has(t.organizationId)) {
        this.orgDDList.push({ Key: t.organizationId.toString(), Value: t.organizationId });
        uniqueCustomerIds.add(t.organizationId);
      }
    });
    this.selectedOrgId = this.orgDDList[0]?.Key;
  }

  filterChange() {
    this.filterData(this.filterText);
  }

  filterData(filter) {
    this.filterText = (filter || '').toLowerCase();
    const text = this.filterText;
    const statusFilter = (this.selectedStatusId || '').toLowerCase();
    const orgFilter = this.selectedOrgId;
    const applyStatus = statusFilter !== 'all' && statusFilter !== '';
    const applyOrg = orgFilter !== 'all' && orgFilter !== '';
    const applySearch = text !== '';
    const systemMatchesSearch = (sys: System): boolean => (
      sys.systemId?.toLowerCase().includes(text) ||
      (sys.systemFriendlyName || '').toLowerCase().includes(text) ||
      this.displaySite(sys.siteId)?.toLowerCase().includes(text)
    );

    const deviceMatchesSearch = (dev: Device): boolean => (
      dev.edgeDeviceId?.toLowerCase().includes(text) ||
      (dev.edgeDeviceFriendlyName || '').toLowerCase().includes(text) ||
      dev.organizationId?.toLowerCase().includes(text)
    );

    // First pass: apply org + status filters (no search pruning) recursively.
    const applyOrgStatus = (device: Device): Device | null => {
      const orgOk = !applyOrg || device.organizationId?.toString() === orgFilter;
      if (!orgOk) {
        // We still evaluate children in case org filter mismatches but a child should not be shown since org mismatch at root.
        // To keep behavior consistent with original flat org filter, drop whole subtree.
        return null;
      }
      let systems: System[] = [...(device.systems || [])];
      const deviceStatusMatches = !applyStatus || device.connectionState?.toLowerCase().includes(statusFilter);
      if (applyStatus && !deviceStatusMatches) {
        systems = systems.filter(s => s.status?.toLowerCase().includes(statusFilter));
      }
      let children: Device[] = [];
      if (device.children?.length) {
        children = device.children.map(applyOrgStatus).filter((c): c is Device => !!c);
      }
      // If after status filtering nothing remains (no systems, no children) and device itself doesn't meet status, drop it.
      if (applyStatus && !deviceStatusMatches && systems.length === 0 && children.length === 0) {
        return null;
      }
      return { ...device, systems, children };
    };

    const statusFilteredRoots: Device[] = [];
    this.rootDevices.forEach(r => {
      const filtered = applyOrgStatus(r);
      if (filtered) statusFilteredRoots.push(filtered);
    });

    // If no search text, show status/org filtered tree.
    if (!applySearch) {
      this.displayedRootDevices = statusFilteredRoots;
      this.provisionedListData = [...statusFilteredRoots];
      this.provisionedList.data = this.provisionedListData;
      return;
    }

    // Search evaluation (if any descendant matches, keep FULL subtree for that root as filtered by status/org).
    const subtreeMatches = (device: Device): boolean => {
      if (deviceMatchesSearch(device)) return true;
      if ((device.systems || []).some(systemMatchesSearch)) return true;
      return (device.children || []).some(subtreeMatches);
    };

    const searchFilteredRoots: Device[] = [];
    statusFilteredRoots.forEach(root => {
      if (subtreeMatches(root)) {
        // Include entire root subtree (already status/org filtered) without further pruning.
        searchFilteredRoots.push(root);
      }
    });

    this.displayedRootDevices = searchFilteredRoots;
    this.provisionedListData = [...searchFilteredRoots];
    this.provisionedList.data = this.provisionedListData;
  }

  compareAsc(a, b) {
    if (a && b) {
      if (a?.toLowerCase() > b?.toLowerCase()) { return 1; }
      if (a?.toLowerCase() < b?.toLowerCase()) { return -1; }
    }
    return 0;
  }

  addNewSystemDialog(deviceConfig: Device) {
    const dialogRef: MatDialogRef<DeploymentFormDialogComponent> =
      this.dialog.open(DeploymentFormDialogComponent,
        {
          disableClose: true,
          backdropClass: 'lgDialog',
          data: { edgeDeviceId: deviceConfig.edgeDeviceId, provisioned: true } as DeploymentFormDialogComponentData
        });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== true) {
        const loadingKey = 'AddNewSystemClosed';
        this.ngxLoader.start(loadingKey);
        setTimeout(() => {
          this.filterChange();
          this.ngxLoader.stop(loadingKey);
        }, 1000);
      }
    });
  }

  showEdgeApplianceDetails(deviceConfig: Device) {
    const dialogRef: MatDialogRef<EdgeApplianceDetailsComponent> =
      this.dialog.open(EdgeApplianceDetailsComponent,
        {
          disableClose: true,
          backdropClass: 'lgDialog',
          data: { edgeDeviceId: deviceConfig.edgeDeviceId }
        });
    let instance = dialogRef.componentInstance;
    instance.parentCompData = deviceConfig;

    dialogRef.afterClosed().subscribe(() => {
      setTimeout(() => {
        this.filterChange();
      }, 1000);
    });
  }

  restartModules(deviceId) {
    const ngxLoaderKey = 'restartModules';
    this.ngxLoader.start(ngxLoaderKey);
    this.fleetManagementService.getRestartModules(deviceId).subscribe(
      (result) => {
        this.ngxLoader.stop(ngxLoaderKey);
        console.log(result);
      },
      (error) => {
        this.toastService.stopLoadingAndShowError('error',
          'An error occurred while restarting modules', ngxLoaderKey);
      });
  }

  restartDevice(deviceId) {
    const ngxLoaderKey = 'restartDevice';
    this.ngxLoader.start(ngxLoaderKey);
    var device = this.provisionedListData.find(dev => dev.edgeDeviceId == deviceId);

    this.fleetManagementService.getRestartDevice(deviceId).subscribe(
      (result) => {
        this.ngxLoader.stop(ngxLoaderKey);
        console.log(result);
        this.toastService.openToast('Restarting Edge Appliance: ' + device.edgeDeviceFriendlyName + ' - ' + deviceId, ToastPanelType.done);
      },
      (error) => {
        this.toastService.stopLoadingAndShowError('error',
          'Unable to complete system restart: ' + device.edgeDeviceFriendlyName + ' - ' + deviceId, ngxLoaderKey);
      });
  }

  // Offline root devices remain eligible; missing, child, and already-disabled devices do not.
  canDisableDevice(device?: Device): boolean {
    return !!device && !device.isChild && device.connectionState !== 'Disabled';
  }

  // Disable an Edge Appliance
  onDisableDevice(device?: Device) {
    // Keep direct calls from bypassing the menu's eligibility rules.
    if (!device || !this.canDisableDevice(device)) { return; }

    const messageHtml = 'Disabling an Edge Appliance will stop data being transferred and data will be lost after 72 hours.<br><br>Are you sure you want to disable the Edge Appliance?';
    const dialogRef = this.dialog.open(GenericConfirmationDialogComponent,
      {
        // Widen dialog for readability while remaining responsive
        width: '600px',
        maxWidth: '90vw',
        disableClose: true
      });
    let instance = dialogRef.componentInstance;
    instance.dialogRef = dialogRef;
    instance.title = `Disable Edge Appliance: ${device.organizationId} - ${device.edgeDeviceFriendlyName}`;
    instance.messageHtml = messageHtml;
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult === true) {
        const ngxLoaderKey = `DisableDevice_${++this.disableDeviceLoaderSequence}`;
        this.ngxLoader.start(ngxLoaderKey);
        this.fleetConfigurationService.disableDevice(device.edgeDeviceId).pipe(
          finalize(() => this.ngxLoader.stop(ngxLoaderKey))
        ).subscribe({
          next: (result) => {
            // The API returns an empty success response; reaching next confirms it was accepted.
            this.store.dispatch(FleetManagerActions.deviceDisabled({ deviceId: device.edgeDeviceId }));
            this.toastService.openToast('Disabled edge device successfully.', constants.ToastPanelType.done);
            console.log(result);
          },
          error: (error) => {
            // Do not mark the device disabled when the API rejects the request.
            this.toastService.stopLoadingAndShowError(error, 'Failed to disable edge device!');
          }
        });
      }
    });
  }
  deprovisionDevice(deviceId) {
    const message = "Are you sure? Removing this device will remove the Customer Data as well as the applications for Stratus for this Customer."
    let matDialogRef = this.dialog.open(GenericConfirmationDialogComponent,
      {
        backdropClass: 'smDialog'
      });

    matDialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult !== true) {
        return;
      }

      const ngxLoaderKey = 'deprovisionDevice';
      this.ngxLoader.start(ngxLoaderKey);

      var device = this.provisionedListData.find(dev => dev.edgeDeviceId === deviceId);

      this.fleetConfigurationService.deprovisionDevice(deviceId).subscribe({
        next: () => {
          setTimeout(() => {
            this.ngxLoader.stop(ngxLoaderKey);
            this.toastService.openToast('Deprovisioned edge device: ' + device.edgeDeviceFriendlyName + ' - ' + deviceId, ToastPanelType.done);
            this.store.dispatch(FleetManagerActions.getIOTDeviceDetails());
          }, 5000);
        },
        error: error => {
          console.error(error);
          this.toastService.stopLoadingAndShowError('error',
            'Unable to deprovision device: ' + device.edgeDeviceFriendlyName + ' - ' + deviceId, ngxLoaderKey);
        }
      });
    });

    let instance = matDialogRef.componentInstance;
    instance.title = "Deprovision Device";
    instance.message = message;
    instance.dialogRef = matDialogRef;
  }

  downloadLogs(deviceId) {
    if (this.downloadingLogs) { return; }
    this.downloadingLogs = true;
    const ngxLoaderKey = 'downloadLogs';
    this.ngxLoader.start(ngxLoaderKey);
    const logOptions: getLogOptions = { tail: '500' };
    this.fleetManagementService.getDeviceLog(deviceId, logOptions).subscribe((result: any) => {
      if (result.size == 0) {
        this.toastService.stopLoadingAndShowError('error',
          'Log file contains no information', ngxLoaderKey);
        this.downloadingLogs = false;
      }
      else {
        this.ngxLoader.stop(ngxLoaderKey);
        const blob = new Blob([result], { type: result.type });
        const blobUrl = window.URL.createObjectURL(blob);
        const fileAnchor = document.createElement('a');
        const filename = `${deviceId}_Logs.zip`;
        fileAnchor.download = decodeURIComponent(filename);
        fileAnchor.href = blobUrl;
        fileAnchor.click();
        this.downloadingLogs = false;
      }
    }, (error) => {
      console.log(error);
      let msgtext = 'An error has occurred';
      if (error.status == 504) {
        msgtext = 'A time out has occurred pulling logs';
      }
      else if (error.status == 500) {
        msgtext = 'Device not responding';
      }
      this.toastService.stopLoadingAndShowError('error',
        msgtext, ngxLoaderKey);
      this.downloadingLogs = false;
    });
  }

  getDeviceDisplayName(device: Device): string {
    return `${device.organizationId} - ${device.edgeDeviceFriendlyName || device.edgeDeviceId}`;
  }

  /* Child deployments collapse/expand handling */
  toggleChildDeployments(device: Device, event?: Event) {
    event?.stopPropagation();
    const id = device.edgeDeviceId;
    if (this.childDeploymentsExpanded.has(id)) {
      this.childDeploymentsExpanded.delete(id);
    } else {
      this.childDeploymentsExpanded.add(id);
    }
  }

  isChildDeploymentsExpanded(device: Device): boolean {
    return this.childDeploymentsExpanded.has(device.edgeDeviceId);
  }

  toggleAccordion() {
    if (this.accordionCollapsed) {
      this.accordionCollapsed = false;
      this.accordion.openAll();
    }
    else {
      this.accordionCollapsed = true;
      this.accordion.closeAll();
    }
  }

  /** Manually refresh provisioned device list from backend/store. */
  refreshDevices() {
    const loaderKey = 'RefreshProvisionedDevices';
    this.ngxLoader.start(loaderKey);
    this.store.dispatch(FleetManagerActions.getIOTDeviceDetails());
    // Give store a moment; adjust delay if needed or hook into selector for precise stop.
    setTimeout(() => this.ngxLoader.stop(loaderKey), 800);
  }
  
  getSystemActions(system: System): SystemAction[] {
    if (this.systemActionsCache.has(system)) {
      return this.systemActionsCache.get(system);
    }

    const actions = (system as any)?.actions ?? (system as any)?.Actions ?? [];
    const normalized = (Array.isArray(actions) ? actions : []).map(action => this.normalizeActionMetadata(action));
    this.systemActionsCache.set(system, normalized);
    return normalized;
  }

  trackByActionName(_index: number, action: SystemAction | DeploymentAction): string {
    return action?.name || action?.label || `${_index}`;
  }

  getDeploymentActions(device: Device): DeploymentAction[] {
    const deviceId = device?.edgeDeviceId;
    if (deviceId && this.deploymentActionsCache.has(deviceId)) {
      return this.deploymentActionsCache.get(deviceId);
    }

    // Source of truth per backend contract: deploymentActions on each provisioned device row.
    const deploymentActions = [
      ...((device as any)?.deploymentActions ?? []),
      ...((device as any)?.DeploymentActions ?? [])
    ];

    const normalized = deploymentActions
      .map(action => this.normalizeActionMetadata(action))
      .filter(action => !!action?.name);

    // De-duplicate by action name (case-insensitive).
    const mapByName = new Map<string, DeploymentAction>();
    normalized.forEach(action => {
      const key = action.name.toLowerCase();
      if (!mapByName.has(key)) {
        mapByName.set(key, action);
      }
    });

    const stable = Array.from(mapByName.values());
    if (deviceId) {
      this.deploymentActionsCache.set(deviceId, stable);
    }

    return stable;
  }

  /**
   * Optional defensive fallback from backend guidance:
   * if deploymentActions are empty when row menu opens, fetch device details once and re-check.
   */
  onDeviceMenuOpened(device: Device): void {
    if (!device?.edgeDeviceId) { return; }
    if (this.getDeploymentActions(device).length > 0) { return; }
    if (this.deploymentActionsChecked.has(device.edgeDeviceId)) { return; }

    this.deploymentActionsChecked.add(device.edgeDeviceId);

    this.fleetManagementService.getEdgeDeviceById(device.edgeDeviceId).subscribe({
      next: (edgeDevice) => {
        if (!edgeDevice) { return; }

        // Keep both property casings in sync to support mixed backend casing.
        (device as any).deploymentActions = edgeDevice?.deploymentActions ?? edgeDevice?.DeploymentActions ?? [];
        (device as any).DeploymentActions = edgeDevice?.DeploymentActions ?? edgeDevice?.deploymentActions ?? [];
        this.deploymentActionsCache.delete(device.edgeDeviceId);
      },
      error: () => {
        // Intentionally silent: missing deployment actions is a valid "not available" state.
      }
    });
  }

  hasAnyRowActions(system: System): boolean {
    return this.getSystemActions(system).length > 0;
  }

  getActionLabel(action: SystemAction | DeploymentAction): string {
    return action?.label || action?.name || 'Action';
  }

  onDeploymentActionClick(device: Device, action: DeploymentAction): void {
    console.log('[ProvisionedComponent] Deployment action clicked', {
      edgeDeviceId: device?.edgeDeviceId,
      action
    });
    this.executeDeploymentAction(device, action);
  }

  executeDeploymentAction(device: Device, action: DeploymentAction): void {
    console.debug('[ProvisionedComponent] Executing deployment action', {
      edgeDeviceId: device?.edgeDeviceId,
      actionName: action?.name,
      actionScope: action?.scope,
      actionUi: action?.ui,
      operationNames: action?.operations?.map(o => o.name) || []
    });

    if (!action?.name) {
      console.warn('[ProvisionedComponent] Deployment action missing name.', { device, action });
      this.toastService.openToast('Action metadata is invalid', ToastPanelType.error);
      return;
    }

    if (this.isCertManagerAction(action)) {
      this.openCertManagerDialog(device, action);
      return;
    }

    const matDialogRef = this.dialog.open(GenericConfirmationDialogComponent, {
      backdropClass: 'smDialog'
    });

    const instance = matDialogRef.componentInstance;
    instance.title = this.getActionLabel(action);
    instance.message = action.confirmMessage || 'Are you sure you want to proceed?';
    instance.dialogRef = matDialogRef;

    matDialogRef.afterClosed().subscribe(ok => {
      if (!ok) return;

      const loaderKey = `ExecuteDeploymentAction_${action.name}_${device.edgeDeviceId}`;
      this.ngxLoader.start(loaderKey);

      this.fleetManagementService
        .executeDeploymentAction(device.edgeDeviceId, action.name)
        .subscribe({
          next: () => {
            this.ngxLoader.stop(loaderKey);
            this.toastService.openToast(`${this.getActionLabel(action)} completed`, ToastPanelType.done);
          },
          error: () => {
            this.ngxLoader.stop(loaderKey);
            this.toastService.openToast(`${this.getActionLabel(action)} failed`, ToastPanelType.error);
          }
        });
    });
  }

  private openCertManagerDialog(device: Device, action: DeploymentAction): void {
    console.debug('[ProvisionedComponent] Opening cert-manager dialog', {
      edgeDeviceId: device?.edgeDeviceId,
      action
    });

    this.dialog.open(CertManagerDialogComponent, {
      disableClose: false,
      width: '50vw',
      maxWidth: '50vw',
      maxHeight: '75vh',
      data: {
        edgeDeviceId: device.edgeDeviceId,
        action
      } as CertManagerDialogData
    });
  }

  private isCertManagerAction(action: DeploymentAction): boolean {
    const actionName = (action?.name || '').toLowerCase();
    const scope = (action?.scope || '').toLowerCase();
    const controlType = (action?.ui?.controlType || '').toLowerCase();
    const controlKey = (action?.ui?.controlKey || '').toLowerCase();

    // Preferred strict metadata match.
    const strictMatch = actionName === this.certManagerActionName.toLowerCase()
      && scope === 'deployment'
      && controlType === this.certManagerControlType
      && controlKey === this.certManagerControlKey;

    // Defensive fallback: backend may temporarily omit scope/ui while still returning action/operations.
    const hasCertOperations = (action?.operations || []).some(op => {
      const opName = (op?.name || '').toLowerCase();
      return opName === 'listcert' || opName === 'installcert' || opName === 'removecert';
    });
    const fallbackMatch = actionName === this.certManagerActionName.toLowerCase() && hasCertOperations;

    if (!strictMatch && fallbackMatch) {
      console.warn('[ProvisionedComponent] Cert-manager action matched by fallback (missing strict scope/ui metadata).', {
        action
      });
    }

    return strictMatch || fallbackMatch;
  }

  private normalizeActionMetadata(action: any): DeploymentAction {
    const operationsRaw = action?.operations ?? action?.Operations ?? [];
    const operations: ExposedActionOperation[] = (Array.isArray(operationsRaw) ? operationsRaw : []).map((op: any) => ({
      name: op?.name ?? op?.Name ?? '',
      label: op?.label ?? op?.friendlyName ?? op?.FriendlyName,
      description: op?.description ?? op?.Description
    })).filter(op => !!op.name);

    const uiRaw = action?.ui ?? action?.Ui ?? {};

    return {
      name: action?.name ?? action?.Name,
      label: action?.label ?? action?.friendlyName ?? action?.FriendlyName ?? action?.name ?? action?.Name,
      description: action?.description ?? action?.Description,
      confirmMessage: action?.confirmMessage ?? action?.ConfirmMessage,
      scope: action?.scope ?? action?.Scope,
      ui: {
        controlType: uiRaw?.controlType ?? uiRaw?.ControlType,
        controlKey: uiRaw?.controlKey ?? uiRaw?.ControlKey,
        metadata: uiRaw?.metadata ?? uiRaw?.Metadata
      },
      operations,
      sharedVolumes: action?.sharedVolumes ?? action?.SharedVolumes
    };
  }

  /** Handle a system-level action click. Calls backend and reports result. */
  executeSystemAction(device: Device, system: System, action: SystemAction) {
    const matDialogRef = this.dialog.open(GenericConfirmationDialogComponent, {
      backdropClass: 'smDialog'
    });

    const instance = matDialogRef.componentInstance;
    instance.title = action.label || action.name;
    instance.message = action.confirmMessage || 'Are you sure you want to proceed?';
    instance.dialogRef = matDialogRef;

    matDialogRef.afterClosed().subscribe(ok => {
      if (!ok) return;

      const loaderKey = `ExecuteSystemAction_${action.name}_${system.systemId}`;
      this.ngxLoader.start(loaderKey);

      const label = action?.label ?? action?.name ?? 'Action';
      this.fleetManagementService
        .executeSystemAction(device.edgeDeviceId, system.systemId, action.name)
        .subscribe({
          next: (res) => {
            this.ngxLoader.stop(loaderKey);
            this.toastService.openToast(`${label} completed`, ToastPanelType.done);
          },
          error: (err) => {
            this.ngxLoader.stop(loaderKey);
            this.toastService.openToast(`${label} failed`, ToastPanelType.error);
          }
        });
    });
  }

}
